<template>
    <div>
        <title-field style="font-size: 24px">
            {{ $t('devis.devis_voyage.titre_stepr_3') }} &nbsp; :
        </title-field>

        <!-- Mode Selector - Always visible until a mode is chosen -->
        <div class="mode-selector" v-if="!captureMode">
            <div class="field-title mb-3">Comment souhaitez-vous saisir les informations du passager ?</div>
            <div class="mode-buttons">
                <button
                    type="button"
                    class="mode-btn"
                    @click="captureMode = 'ocr'"
                >
                    <i class="fas fa-camera"></i> Scanner le passeport
                </button>
                <button
                    type="button"
                    class="mode-btn"
                    @click="captureMode = 'manual'"
                >
                    <i class="fas fa-edit"></i> Saisie manuelle
                </button>
            </div>
        </div>

        <!-- OCR Mode: Upload Section -->
        <div v-if="captureMode === 'ocr' && ocrStatus !== 'success'" class="upload-section">
            <label class="drop-container">
                <i class="fas fa-passport upload-icon"></i>
                <span class="upload-text">{{ $t('devis.devis_voyage.passenger.upload_passport') }}</span>
                <input
                    type="file"
                    @change="onFileSelected"
                    accept="image/jpeg,image/png,image/jpg"
                    :disabled="ocrLoading"
                    class="file-input"
                >
            </label>
            <div v-if="ocrLoading" class="ocr-status loading">
                <i class="fas fa-spinner fa-spin"></i> {{ $t('devis.devis_voyage.passenger.ocr_processing') }}
            </div>
            <div v-if="ocrStatus === 'error'" class="ocr-status error">
                <i class="fas fa-exclamation-circle"></i> {{ ocrErrorMessage }}
            </div>
        </div>

        <!-- OCR Mode: Success Recap -->
        <div v-if="captureMode === 'ocr' && ocrStatus === 'success'" class="ocr-success-section">
            <div class="alert alert-success mb-3" style="background-color: #d4edda; border-left: 4px solid #28a745; padding: 12px;">
                <i class="fas fa-check-circle mr-2"></i>Passeport scanné avec succès !
                <button type="button" @click="resetOcr" class="wizard-btn ml-3" style="background-color: #6c757d; color: white; padding: 6px 18px; border-radius: 50px;">
                    <i class="fas fa-redo mr-2"></i>Réessayer
                </button>
                <button v-if="!isEditingOcr" type="button" @click="editOcr" class="wizard-btn ml-2" style="background-color: #ffa409; color: white; padding: 6px 18px; border-radius: 50px;">
                    <i class="fas fa-edit mr-2"></i>Modifier
                </button>
            </div>

            <!-- Récap lecture seule -->
            <div v-if="!isEditingOcr" class="passport-recap" style="background: #f8f9fa; border-radius: 10px; padding: 15px;">
                <div class="row">
                    <div class="col-6 mb-2">
                        <small class="text-muted">Nom</small><br>
                        <span class="font-weight-bold">{{ passportData.nom || '—' }}</span>
                    </div>
                    <div class="col-6 mb-2">
                        <small class="text-muted">Prénom</small><br>
                        <span class="font-weight-bold">{{ passportData.prenom || '—' }}</span>
                    </div>
                    <div class="col-6 mb-2">
                        <small class="text-muted">N° Passeport</small><br>
                        <span class="font-weight-bold">{{ passportData.numeroPasseport || '—' }}</span>
                    </div>
                    <div class="col-6 mb-2">
                        <small class="text-muted">Date de naissance</small><br>
                        <span class="font-weight-bold">{{ passportData.dateNaissance || '—' }}</span>
                    </div>
                    <div class="col-6 mb-2">
                        <small class="text-muted">Date d'expiration</small><br>
                        <span class="font-weight-bold">{{ passportData.dateExpiration || '—' }}</span>
                    </div>
                    <div class="col-6 mb-2">
                        <small class="text-muted">Âge</small><br>
                        <span class="font-weight-bold">{{ passengerAge }} an(s)</span>
                    </div>
                </div>
            </div>

            <!-- Formulaire d'édition pré-rempli -->
            <div v-else class="passport-edit" style="background: #f8f9fa; border-radius: 10px; padding: 15px;">
                <div class="row">
                    <div class="col-sm-6 mb-3">
                        <SubTitle style="display: block">Nom</SubTitle>
                        <FieldInput type="text" style="width: 100%" v-model="passportData.nom" />
                    </div>
                    <div class="col-sm-6 mb-3">
                        <SubTitle style="display: block">Prénom</SubTitle>
                        <FieldInput type="text" style="width: 100%" v-model="passportData.prenom" />
                    </div>
                    <div class="col-sm-6 mb-3">
                        <SubTitle style="display: block">N° Passeport</SubTitle>
                        <FieldInput type="text" style="width: 100%" v-model="passportData.numeroPasseport" />
                    </div>
                    <div class="col-sm-6 mb-3">
                        <SubTitle style="display: block">Date de naissance</SubTitle>
                        <DateInput
                            :key="'edit-birth-' + dateInputKey"
                            :dateToDisabled="new Date()"
                            @valueDate="onEditBirthDate"
                            :default-date="passportData.dateNaissance"
                        ></DateInput>
                    </div>
                    <div class="col-sm-6 mb-3">
                        <SubTitle style="display: block">Date d'expiration</SubTitle>
                        <DateInput
                            :key="'edit-exp-' + dateInputKey"
                            @valueDate="onEditExpirationDate"
                            :default-date="passportData.dateExpiration"
                        ></DateInput>
                    </div>
                    <div class="col-sm-6 mb-3">
                        <SubTitle style="display: block">Âge</SubTitle>
                        <FieldInput type="number" disabled v-model="passengerAge" style="width: 100%" />
                    </div>
                </div>
                <div class="text-center mt-3">
                    <button type="button" @click="cancelOcrEdit" class="wizard-btn mr-3" style="background-color: #6c757d; color: white; padding: 10px 25px; border-radius: 50px;">
                        <i class="fas fa-times mr-2"></i>Annuler
                    </button>
                    <button type="button" @click="saveOcrEdit" class="wizard-btn" style="background-color: #ffa409; color: white; padding: 10px 25px; border-radius: 50px;">
                        <i class="fas fa-check mr-2"></i>Enregistrer
                    </button>
                </div>
            </div>
        </div>

        <!-- Manual Mode: Original Fields -->
        <div v-if="captureMode === 'manual'" class="manual-section">
            <div class="row">
                <div class="col-sm-4">
                    <SubTitle style="display: block">
                        {{ $t('devis.devis_voyage.dateVoyage.Headline_3') }}
                    </SubTitle>
                    <FieldInput
                        type="number"
                        style="width: 100%"
                        v-model="passengerNumber"
                        readonly
                    />
                </div>

                <div class="col-sm-4">
                    <sub-title style="display: block">
                        {{ $t('devis.devis_voyage.dateVoyage.Headline_4') }}
                    </sub-title>
                    <DateInput
                        :key="'birth-' + dateInputKey"
                        :dateToDisabled="new Date()"
                        @valueDate="birthdayPassengerVal"
                        :default-date="defaultBirthdayValue"
                    ></DateInput>
                </div>

                <div class="col-sm-4">
                    <SubTitle style="display: block">
                        {{ $t('devis.devis_voyage.dateVoyage.Headline_5') }}
                    </SubTitle>
                    <div style="display: flex;">
                        <FieldInput
                            type="number"
                            disabled
                            v-model="passengerAge"
                            style="flex: 6;"
                        />
                        <div class="ml-2 prepend-birthday" style="flex: 2;">
                            {{$t('devis.devis_voyage.dateVoyage.Headline_6')}}
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Footer Buttons -->
        <div class="bottom-button wizard-footer-left" v-if="captureMode">
            <button
                type="button"
                class="wizard-btn"
                @click="decrementStepper()"
            >
                <span class="fas fa-arrow-left"></span>
                {{$t('devis.devis_voyage.bouton_retour')}}
            </button>

            <button
                type="button"
                class="filled-button"
                @click="validatePassenger()"
            >
                {{$t('devis.devis_voyage.bouton_suivant')}}
            </button>
        </div>

        <!-- Footer for initial mode selection -->
        <div class="bottom-button wizard-footer-left" v-if="!captureMode">
            <button
                type="button"
                class="wizard-btn"
                @click="decrementStepper()"
            >
                <span class="fas fa-arrow-left"></span>
                {{$t('devis.devis_voyage.bouton_retour')}}
            </button>
        </div>
    </div>
</template>

<script>
import ToastMixin from '../../../mixin/ToastMixin';
import DateInput from '../../layouts/FormInput/DateInput.vue';
import FieldInput from '../../layouts/FormInput/FieldInput.vue';
import SubTitle from '../../layouts/texts/SubTitle.vue';
import { mapMutations } from 'vuex';
import TitleField from '../../layouts/texts/TitleField.vue';
import dayjs from 'dayjs';
import PassportOcrService from '@/services/PassportOcrService';

    export default {
        name: "PassengerComponent",
        mixins: [ToastMixin],
        components: {
            FieldInput,
            SubTitle,
            DateInput,
            TitleField
        },
        data() {
            return {
                // Original data
                passengerNumber: 1,
                birthdayPassenger: null,
                passengerAge: 0,
                defaultValueBirthday: "",

                // OCR data
                captureMode: null,              // null | 'ocr' | 'manual'
                ocrLoading: false,
                ocrStatus: 'idle',              // 'idle' | 'success' | 'error'
                ocrErrorMessage: '',
                dateInputKey: 0,                // Key to force DateInput re-render
                isEditingOcr: false,            // true pendant l'édition du récap OCR
                passportDataBackup: null,       // snapshot avant édition pour annuler
                passportData: {
                    nom: '',
                    prenom: '',
                    numeroPasseport: '',
                    dateNaissance: '',
                    dateExpiration: ''
                }
            }
        },
        computed: {
            defaultBirthdayValue() {
                let container = this.$store.state.devisVoyageContainer
                if (container.passenger) {
                    this.birthdayPassengerVal(container.passenger.birthday.date)
                    return container.passenger.birthday.date
                }
                return ""
            }
        },
        methods: {
            ...mapMutations([
                "incrementStepper",
                "decrementStepper",
                "mutateContainerDevisVoyage"
            ]),

            birthdayPassengerVal(date) {
                let today = new Date();
                this.birthdayPassenger = date;
                this.passengerAge = dayjs(today).diff(date, 'year');
            },

            resetMode() {
                this.captureMode = null;
                this.ocrStatus = 'idle';
                this.ocrLoading = false;
                this.ocrErrorMessage = '';
                this.isEditingOcr = false;
                this.passportDataBackup = null;
                this.passportData = {
                    nom: '',
                    prenom: '',
                    numeroPasseport: '',
                    dateNaissance: '',
                    dateExpiration: ''
                };
            },

            async onFileSelected(event) {
                const input = event.target;
                const file = input.files[0];
                if (!file) return;

                const validation = PassportOcrService.validateFile(file);
                if (!validation.valid) {
                    this.ocrStatus = 'error';
                    this.ocrErrorMessage = this.getErrorMessage(validation.error);
                    input.value = '';
                    return;
                }

                this.ocrLoading = true;
                this.ocrStatus = 'idle';

                try {
                    var result = await PassportOcrService.scanPassport(file);
                    this.passportData = Object.assign({}, this.passportData, result);
                    this.ocrStatus = 'success';

                    // Calculate age from OCR date
                    if (result.dateNaissance) {
                        this.birthdayPassengerVal(result.dateNaissance);
                    }
                } catch (error) {
                    this.ocrStatus = 'error';
                    this.ocrErrorMessage = this.getErrorMessage(error.message);
                } finally {
                    this.ocrLoading = false;
                    input.value = '';
                }
            },

            getErrorMessage(code) {
                const messages = {
                    'NO_FILE': this.$t('devis.devis_voyage.passenger.error_no_file'),
                    'INVALID_TYPE': this.$t('devis.devis_voyage.passenger.error_invalid_type'),
                    'FILE_TOO_LARGE': this.$t('devis.devis_voyage.passenger.error_file_too_large'),
                    'OCR_INCOMPLETE_DATA': this.$t('devis.devis_voyage.passenger.error_incomplete')
                };
                return messages[code] || this.$t('devis.devis_voyage.passenger.error_generic');
            },

            resetOcr() {
                this.ocrStatus = 'idle';
                this.ocrLoading = false;
                this.ocrErrorMessage = '';
                this.isEditingOcr = false;
                this.passportDataBackup = null;
                this.passportData = {
                    nom: '',
                    prenom: '',
                    numeroPasseport: '',
                    dateNaissance: '',
                    dateExpiration: ''
                };
            },

            editOcr() {
                this.passportDataBackup = Object.assign({}, this.passportData);
                this.isEditingOcr = true;
            },

            cancelOcrEdit() {
                if (this.passportDataBackup) {
                    this.passportData = Object.assign({}, this.passportDataBackup);
                    if (this.passportData.dateNaissance) {
                        this.birthdayPassengerVal(this.passportData.dateNaissance);
                    }
                }
                this.passportDataBackup = null;
                this.isEditingOcr = false;
                this.dateInputKey++;
            },

            saveOcrEdit() {
                if (!this.passportData.dateNaissance) {
                    return this.$toasted.show("Veuillez saisir la date de naissance!", this.ToastTabError);
                }
                this.passportDataBackup = null;
                this.isEditingOcr = false;
            },

            onEditBirthDate(date) {
                this.passportData.dateNaissance = date ? dayjs(date).format("YYYY-MM-DD") : '';
                if (this.passportData.dateNaissance) {
                    this.birthdayPassengerVal(this.passportData.dateNaissance);
                }
            },

            onEditExpirationDate(date) {
                this.passportData.dateExpiration = date ? dayjs(date).format("YYYY-MM-DD") : '';
            },

            validatePassenger() {
                // Use OCR date or manual date
                const birthDate = this.passportData.dateNaissance || this.birthdayPassenger;

                if (birthDate === null || birthDate === '') {
                    return this.$toasted.show("Veuillez saisir la date de naissance!", this.ToastTabError);
                }

                // For OCR mode, require successful scan
                if (this.captureMode === 'ocr' && this.ocrStatus !== 'success') {
                    return this.$toasted.show("Veuillez scanner votre passeport!", this.ToastTabError);
                }

                const passengerData = {
                    number: { value: this.passengerNumber },
                    birthday: {
                        date: dayjs(birthDate).format("YYYY-MM-DD"),
                        value: this.passengerAge >= 18 ? `Adulte(s) ${this.passengerAge} an(s)` : `Enfant(s) ${this.passengerAge} an(s)`
                    }
                };

                // Include passport data and OCR flag
                if (this.captureMode === 'ocr' && this.ocrStatus === 'success') {
                    passengerData.passport = {
                        nom: this.passportData.nom,
                        prenom: this.passportData.prenom,
                        numeroPasseport: this.passportData.numeroPasseport,
                        dateExpiration: this.passportData.dateExpiration ?
                            dayjs(this.passportData.dateExpiration).format("YYYY-MM-DD") : ''
                    };
                    passengerData.passportOcrDone = true;
                } else {
                    // Manual mode - no passport data yet, will be collected at subscription
                    passengerData.passportOcrDone = false;
                }

                this.mutateContainerDevisVoyage({ passenger: passengerData });
                this.incrementStepper();
            }
        },
        mounted() {
            window.scrollTo(0, 0);

            // Restore passport data if exists
            var container = this.$store.state.devisVoyageContainer;
            if (container.passenger && container.passenger.passport) {
                this.passportData = Object.assign({}, this.passportData, container.passenger.passport);
                if (container.passenger.birthday && container.passenger.birthday.date) {
                    this.birthdayPassengerVal(container.passenger.birthday.date);
                }
                this.captureMode = 'ocr';
                this.ocrStatus = 'success';
            } else if (container.passenger) {
                // Manual mode was used
                this.captureMode = 'manual';
            }
        }
    }
</script>

<style scoped>
    .mode-selector {
        text-align: center;
        padding: 30px 0;
    }

    .field-title {
        font-size: 1.2rem;
        color: #333;
    }

    .mode-buttons {
        display: flex;
        justify-content: center;
        gap: 20px;
        flex-wrap: wrap;
    }

    .mode-btn {
        padding: 25px 40px;
        border-radius: 15px;
        border: 2px solid #d9dee3;
        background-color: #fff;
        color: #333;
        font-size: 1.1rem;
        cursor: pointer;
        transition: all 0.3s ease;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 12px;
        min-width: 180px;
    }

    .mode-btn i {
        font-size: 2.5rem;
        color: #ffa409;
    }

    .mode-btn:hover {
        border-color: #ffa409;
        background-color: #fff8f0;
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(255, 164, 9, 0.2);
    }

    .upload-section {
        margin-bottom: 25px;
    }

    .drop-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 50px;
        border: 2px dashed #d9dee3;
        border-radius: 15px;
        background-color: #f8f9fa;
        cursor: pointer;
        transition: all 0.3s ease;
    }

    .drop-container:hover {
        border-color: #ffa409;
        background-color: #fff8f0;
    }

    .upload-icon {
        font-size: 60px;
        color: #ffa409;
        margin-bottom: 15px;
    }

    .upload-text {
        font-size: 1.2rem;
        color: #666;
    }

    .file-input {
        display: none;
    }

    .ocr-status {
        margin-top: 15px;
        padding: 12px 20px;
        border-radius: 10px;
        font-size: 1rem;
        display: flex;
        align-items: center;
        gap: 10px;
    }

    .ocr-status.loading {
        background-color: #e3f2fd;
        color: #1976d2;
    }

    .ocr-status.success {
        background-color: #e8f5e9;
        color: #388e3c;
    }

    .ocr-status.error {
        background-color: #ffebee;
        color: #d32f2f;
    }

    .prepend-birthday {
        background-color: #fff;
        border-radius: 15px;
        border: 1px solid #d9dee3;
        text-align: center;
        padding: 15px 0;
    }

    .bottom-button {
        display: flex;
        justify-content: space-between;
        align-items: center;
        height: 150px;
    }

    .filled-button {
        right: 0;
        padding: 12px 40px;
        border-radius: 50px;
        color: #fff;
        border: 1px solid #ffa409;
        background-color: #ffa409;
        font-size: 1.8rem;
        text-transform: uppercase;
        font-weight: 400;
    }

    .mt-3 {
        margin-top: 1rem;
    }

    .mb-3 {
        margin-bottom: 1rem;
    }
</style>
